import { useState } from "react";
import { Share2, Check, Copy, X, MessageCircle, Facebook, Linkedin, Send, Twitter, Share } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Universal share menu. Builds a crawler-safe `/api/share/{slug}?ref={code}` affiliate link
 * (or uses a direct `url` if provided), then offers WhatsApp, Facebook, Twitter/X, LinkedIn,
 * Telegram, WeChat, AirDrop (native share sheet) and Copy link.
 */
export function ShareButton({ slug, url: directUrl, title = "My Kind", text = "", className = "", label = "Share" }) {
  const { user } = useApp();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const ref = user?.referral_code || "";
  const url = directUrl || `${BACKEND}/api/share/${slug}${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`;
  const shareText = text || "Meet My Kind — the sovereign, ad-free, AI-native platform.";
  const U = encodeURIComponent(url);
  const T = encodeURIComponent(shareText);

  const targets = [
    { id: "whatsapp", label: "WhatsApp", Icon: MessageCircle, href: `https://wa.me/?text=${T}%20${U}` },
    { id: "facebook", label: "Facebook", Icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${U}` },
    { id: "twitter", label: "X / Twitter", Icon: Twitter, href: `https://twitter.com/intent/tweet?text=${T}&url=${U}` },
    { id: "telegram", label: "Telegram", Icon: Send, href: `https://t.me/share/url?url=${U}&text=${T}` },
    { id: "linkedin", label: "LinkedIn", Icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${U}` },
  ];

  const openTarget = (href) => { window.open(href, "_blank", "noopener,noreferrer"); setOpen(false); };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title, text: shareText, url }); setOpen(false); return; } catch (_) { /* cancelled */ }
    } else { copy("Link copied — AirDrop/native share isn't available on this device 🔗"); }
  };

  const copy = async (msg = "Link copied 🔗") => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2500); toast.success(msg); }
    catch { toast.error("Couldn't copy — try again."); }
  };

  const onMain = async () => {
    if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
      try { await navigator.share({ title, text: shareText, url }); return; } catch (_) { /* fall to menu */ }
    }
    setOpen((o) => !o);
  };

  return (
    <div className="relative inline-block">
      <button data-testid={`share-page-${slug || "item"}`} onClick={onMain}
        className={className || "btn-primary rounded-full h-10 px-6 text-sm flex items-center gap-2"}>
        <Share2 size={15} /> {label}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-2 left-0 w-72 rud-card rounded-2xl p-4 shadow-xl" data-testid={`share-menu-${slug || "item"}`}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Share {title}</div>
              <button onClick={() => setOpen(false)} data-testid="share-menu-close" className="text-sec hover:text-brand"><X size={16} /></button>
            </div>
            {ref && !directUrl && (
              <p className="text-[11px] text-sec mt-1 flex items-start gap-1.5">
                🎁 Shared with <b className="text-brand">your affiliate link</b> — you earn when friends join.
              </p>
            )}
            <div className="grid grid-cols-2 gap-2 mt-3">
              {targets.map(({ id, label, Icon, href }) => (
                <button key={id} data-testid={`share-${id}`} onClick={() => openTarget(href)}
                  className="flex items-center gap-2 rounded-xl border border-rud p-2.5 text-sm hover:border-brand hover:bg-brand/5 transition-colors">
                  <Icon size={16} className="text-brand shrink-0" /> {label}
                </button>
              ))}
              <button data-testid="share-wechat" onClick={() => copy("Link copied — open WeChat and paste to share 💬")}
                className="flex items-center gap-2 rounded-xl border border-rud p-2.5 text-sm hover:border-brand hover:bg-brand/5 transition-colors">
                <MessageCircle size={16} className="text-brand shrink-0" /> WeChat
              </button>
              <button data-testid="share-airdrop" onClick={nativeShare}
                className="flex items-center gap-2 rounded-xl border border-rud p-2.5 text-sm hover:border-brand hover:bg-brand/5 transition-colors">
                <Share size={16} className="text-brand shrink-0" /> AirDrop / More
              </button>
              <button data-testid="share-copy" onClick={() => copy()}
                className="flex items-center gap-2 rounded-xl border border-rud p-2.5 text-sm hover:border-brand hover:bg-brand/5 transition-colors col-span-2">
                {copied ? <Check size={16} className="text-brand shrink-0" /> : <Copy size={16} className="text-brand shrink-0" />} {copied ? "Copied" : "Copy link"}
              </button>
            </div>
            {ref && !directUrl && <p className="text-[10px] text-sec mt-3 break-all">Your link: {url}</p>}
          </div>
        </>
      )}
    </div>
  );
}

export default ShareButton;
